/**
 * Audio-Analyse (Whisper/Diktat-Pipeline) → Analyse-Dokument auf Nextcloud + Editor-Öffnen.
 * Pattern analog Phase 1.4 Document-Ingest.
 */

import {
  PORTAL_DOCUMENT_UPLOAD_URL,
  hasPortalAuthzToken,
  portalAuthzHeaders,
  resolveOnlyofficeIssueJwtUrl,
} from '../../constants';
import {
  fileExtensionFromName,
  isAudioFile,
  parsePortalDocumentN8nResponse,
  type PortalDocumentOpenFields,
} from './portalDocuments';
import { formatPortalFileSize, PORTAL_CHAT_ANALYZE_MAX_FILE_BYTES } from './portalChatDocumentAttach';

const DIKTAT_UPLOAD_URL = '/webhook/diktat-upload';
const ANALYSIS_UPLOAD_FOLDER = 'audio-analyse';
const MAX_ANALYSIS_TEXT_CHARS = 120_000;

export interface PortalDiktatUploadResponse {
  ok?: boolean;
  error?: boolean;
  message?: string;
  pdfUrl?: string;
  pb_record_id?: string;
  pb_id?: string;
  filename?: string;
  protokollText?: string;
  transcriptText?: string;
  analysis_pb_record_id?: string;
  analysis_document_url?: string;
  analysis_display_name?: string;
  analysis_nextcloud_path?: string;
}

export function validatePortalAudioAnalyzeFile(file: File): string | null {
  if (!isAudioFile(file)) return 'Keine Audiodatei erkannt.';
  if (file.size > PORTAL_CHAT_ANALYZE_MAX_FILE_BYTES) {
    return `Zu groß (max. ${formatPortalFileSize(PORTAL_CHAT_ANALYZE_MAX_FILE_BYTES)}).`;
  }
  if (file.size === 0) return 'Leere Datei.';
  return null;
}

function stripAudioExtension(name: string): string {
  const base = name.replace(/\.[^.]+$/, '').trim();
  return base || 'Audio-Analyse';
}

function buildAnalysisFileName(sourceName: string): string {
  const stamp = new Date().toISOString().slice(0, 10);
  return `Analyse_${stripAudioExtension(sourceName).replace(/[^\wäöüÄÖÜß.-]+/g, '_').slice(0, 48)}_${stamp}.txt`;
}

function buildAnalysisDocumentBody(
  sourceName: string,
  protokollText?: string,
  transcriptText?: string,
): string {
  const lines: string[] = [
    `Audio-Analyse: ${sourceName}`,
    `Erstellt: ${new Date().toLocaleString('de-AT')}`,
    '',
  ];
  if (protokollText?.trim()) {
    lines.push('=== Protokoll / Analyse ===', '', protokollText.trim(), '');
  }
  if (transcriptText?.trim() && transcriptText.trim() !== protokollText?.trim()) {
    lines.push('=== Transkript (Whisper) ===', '', transcriptText.trim(), '');
  }
  if (!protokollText?.trim() && !transcriptText?.trim()) {
    lines.push('Kein Analyse-Text vom Server erhalten.', '');
  }
  lines.push(
    '---',
    'KI-generiert — vor rechtsrelevanter Verwendung prüfen.',
  );
  return lines.join('\n').slice(0, MAX_ANALYSIS_TEXT_CHARS);
}

export async function submitPortalAudioForAnalysis(args: {
  file: File;
  customerId: string;
  kanzlei?: string;
  email?: string;
  signal?: AbortSignal;
}): Promise<PortalDiktatUploadResponse> {
  const { file, customerId, kanzlei, email, signal } = args;
  const err = validatePortalAudioAnalyzeFile(file);
  if (err) throw new Error(err);

  const formData = new FormData();
  formData.append('file', file);
  formData.append('customerId', customerId);
  if (kanzlei?.trim()) formData.append('kanzlei', kanzlei.trim());
  if (email?.trim()) formData.append('email', email.trim());

  const res = await fetch(DIKTAT_UPLOAD_URL, {
    method: 'POST',
    body: formData,
    signal: signal ?? AbortSignal.timeout(300_000),
  });

  let data: PortalDiktatUploadResponse = {};
  try {
    data = (await res.json()) as PortalDiktatUploadResponse;
  } catch {
    /* leer */
  }

  if (!res.ok || data.error) {
    throw new Error(data.message?.trim() || `Audio-Analyse fehlgeschlagen (${res.status}).`);
  }

  return data;
}

export async function uploadPortalAnalysisTextDocument(args: {
  fileName: string;
  content: string;
  customerId: string;
  tenantExtras?: Record<string, string>;
  signal?: AbortSignal;
}): Promise<PortalDocumentOpenFields> {
  const { fileName, content, customerId, tenantExtras = {}, signal } = args;
  if (!hasPortalAuthzToken()) {
    throw new Error('Upload: Bitte neu anmelden (kein gültiges Portal-Token).');
  }

  const file = new File([content], fileName, { type: 'text/plain;charset=utf-8' });
  const fd = new FormData();
  fd.append('file', file);
  fd.set('customerId', customerId);
  fd.set('folderPath', ANALYSIS_UPLOAD_FOLDER);
  if (tenantExtras.tenant) fd.set('tenant', tenantExtras.tenant);

  const url = resolveOnlyofficeIssueJwtUrl(PORTAL_DOCUMENT_UPLOAD_URL);
  const res = await fetch(url, {
    method: 'POST',
    body: fd,
    credentials: 'omit',
    headers: {
      ...portalAuthzHeaders(url),
    },
    signal,
  });
  let data: Record<string, unknown> = {};
  try {
    data = (await res.json()) as Record<string, unknown>;
  } catch {
    /* leer */
  }
  return parsePortalDocumentN8nResponse(data, res);
}

export function buildAnalysisFieldsFromDiktatResponse(
  sourceName: string,
  diktat: PortalDiktatUploadResponse,
  uploaded?: PortalDocumentOpenFields,
): PortalDocumentOpenFields | null {
  if (uploaded?.document_url) return uploaded;

  const analysisUrl = diktat.analysis_document_url?.trim();
  if (!analysisUrl) return null;

  return {
    display_name:
      diktat.analysis_display_name?.trim() ||
      buildAnalysisFileName(sourceName),
    document_url: analysisUrl,
    pb_record_id:
      diktat.analysis_pb_record_id?.trim() ||
      diktat.pb_record_id?.trim() ||
      diktat.pb_id?.trim(),
    nextcloud_path: diktat.analysis_nextcloud_path?.trim(),
  };
}

/**
 * Audio → Diktat-Webhook → Analyse-Text als Portal-Dokument.
 */
export async function prepareAudioAnalyzeDocumentContext(args: {
  file: File;
  customerId: string;
  kanzlei?: string;
  email?: string;
  tenantExtras?: Record<string, string>;
  signal?: AbortSignal;
}): Promise<{
  documentContext: string;
  fileNames: string[];
  uploadedFields: PortalDocumentOpenFields[];
  diktatResponse: PortalDiktatUploadResponse;
}> {
  const { file, customerId, kanzlei, email, tenantExtras, signal } = args;

  const diktat = await submitPortalAudioForAnalysis({
    file,
    customerId,
    kanzlei,
    email,
    signal,
  });

  const analysisText = buildAnalysisDocumentBody(
    file.name,
    diktat.protokollText,
    diktat.transcriptText,
  );

  let uploaded: PortalDocumentOpenFields | undefined;
  if (analysisText.replace(/\s+/g, '').length >= 20) {
    uploaded = await uploadPortalAnalysisTextDocument({
      fileName: buildAnalysisFileName(file.name),
      content: analysisText,
      customerId,
      tenantExtras,
      signal,
    });
  }

  const fields =
    buildAnalysisFieldsFromDiktatResponse(file.name, diktat, uploaded) ||
    (diktat.pdfUrl
      ? {
          display_name: diktat.filename?.trim() || buildAnalysisFileName(file.name).replace('.txt', '.pdf'),
          document_url: diktat.pdfUrl,
          pb_record_id: diktat.pb_record_id?.trim() || diktat.pb_id?.trim(),
        }
      : null);

  const uploadedFields = fields ? [fields] : [];
  const ext = fields ? fileExtensionFromName(fields.display_name) : 'txt';

  const documentContext = [
    '=== Audio-Analyse ===',
    '',
    `Quelldatei: ${file.name}`,
    `Typ: ${ext}`,
    '',
    'Auszug:',
    analysisText.slice(0, 7500),
  ].join('\n');

  return {
    documentContext,
    fileNames: [file.name],
    uploadedFields,
    diktatResponse: diktat,
  };
}
